import { z } from "zod";
import { asinFilterQueryValues, csvQueryValues } from "./dashboard";
import { productCatalogStateSchema, productStatusSchema } from "./product-listing";
import { validateReportingDateRange } from "./reporting";

const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, "Invalid date");

export const productCenterDimensionSchema = z.enum(["SKU", "ASIN", "PARENT_ASIN", "PRODUCT_LINE"]);
export type ProductCenterDimension = z.infer<typeof productCenterDimensionSchema>;

const productLineSelectionDimensionSchema = z.enum(["SKU", "ASIN", "PARENT_ASIN"]);

export const productLineSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
});
export type ProductLineSummary = z.infer<typeof productLineSummarySchema>;

export const productLineSelectionSchema = z.object({
  dimension: productLineSelectionDimensionSchema,
  keys: z.array(z.string().trim().min(1).max(200)).min(1).max(500),
});
export type ProductLineSelection = z.infer<typeof productLineSelectionSchema>;

export const createProductLineBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  selection: productLineSelectionSchema.optional(),
});
export type CreateProductLineBody = z.infer<typeof createProductLineBodySchema>;

export const assignProductLineBodySchema = z.object({ selection: productLineSelectionSchema });
export type AssignProductLineBody = z.infer<typeof assignProductLineBodySchema>;

export const productLineParamsSchema = z.object({ productLineId: z.string().uuid() });

export const productCenterShopSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  marketplaceName: z.string().nullable().optional(),
});

export const productCenterScopeQuerySchema = z.object({
  shopIds: csvQueryValues(z.string().uuid()).optional(),
});

export const productLineSchema = productLineSummarySchema.extend({
  shop: productCenterShopSchema.optional(),
  productCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ProductLine = z.infer<typeof productLineSchema>;

export const productLinesResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(productLineSchema),
});

export const productLineResponseSchema = z.object({
  success: z.literal(true),
  data: productLineSchema,
});

export const productCenterSortSchema = z.enum([
  "identity",
  "title",
  "impressions",
  "clicks",
  "spend",
  "adSales",
  "orders",
  "acos",
  "cpc",
  "cpa",
]);
export type ProductCenterSort = z.infer<typeof productCenterSortSchema>;

const productLineFilterValueSchema = z.union([z.string().uuid(), z.literal("unassigned")]);
const productLineIdsSchema = z.preprocess(
  (value) =>
    typeof value === "string"
      ? value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      : value,
  z
    .array(productLineFilterValueSchema)
    .min(1)
    .max(50)
    .transform((values) => [...new Set(values)]),
);

export const productCenterProductsQuerySchema = z
  .object({
    shopIds: csvQueryValues(z.string().uuid()).optional(),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    dimension: productCenterDimensionSchema.default("SKU"),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
    search: z.string().trim().max(200).optional(),
    active: z.enum(["true", "false", "all"]).default("true"),
    availability: z.string().trim().min(1).max(100).optional(),
    productStatus: z.enum(["advertised", "unadvertised"]).optional(),
    productLineIds: productLineIdsSchema.optional(),
    // Exact listing-ASIN filter (not a substring search like `search`).
    asins: asinFilterQueryValues(),
    sortBy: productCenterSortSchema.default("identity"),
    sortDirection: z.enum(["asc", "desc"]).default("asc"),
  })
  .superRefine(validateReportingDateRange);
export type ProductCenterProductsQuery = z.infer<typeof productCenterProductsQuerySchema>;

export const productCenterAdsMetricsSchema = z.object({
  status: z.enum(["available", "partial", "unavailable"]),
  impressions: z.number().int().nonnegative(),
  clicks: z.number().int().nonnegative(),
  spend: z.number().nonnegative(),
  adSales: z.number().nonnegative(),
  orders: z.number().int().nonnegative(),
  acos: z.number().nonnegative().nullable(),
  cpc: z.number().nonnegative().nullable(),
  cpa: z.number().nonnegative().nullable(),
  coveredDays: z.number().int().nonnegative(),
  expectedDays: z.number().int().positive(),
});
export type ProductCenterAdsMetrics = z.infer<typeof productCenterAdsMetricsSchema>;

export const unavailableProductCenterSellerMetricsSchema = z.object({
  status: z.literal("unavailable"),
  sales: z.null(),
  orders: z.null(),
  units: z.null(),
  sessions: z.null(),
  pageViews: z.null(),
  buyBoxPercentage: z.null(),
  conversionRate: z.null(),
  coveredDays: z.literal(0),
  expectedDays: z.number().int().positive(),
});
export type UnavailableProductCenterSellerMetrics = z.infer<
  typeof unavailableProductCenterSellerMetricsSchema
>;

export const measuredProductCenterSellerMetricsSchema = z.object({
  status: z.enum(["available", "partial"]),
  sales: z.number().nonnegative(),
  orders: z.number().int().nonnegative(),
  units: z.number().int().nonnegative(),
  sessions: z.number().int().nonnegative(),
  pageViews: z.number().int().nonnegative(),
  buyBoxPercentage: z.number().min(0).max(1).nullable(),
  conversionRate: z.number().nonnegative().nullable(),
  coveredDays: z.number().int().positive(),
  expectedDays: z.number().int().positive(),
});

export const productCenterSellerMetricsSchema = z.discriminatedUnion("status", [
  unavailableProductCenterSellerMetricsSchema,
  measuredProductCenterSellerMetricsSchema,
]);
export type ProductCenterSellerMetrics = z.infer<typeof productCenterSellerMetricsSchema>;

export const productCenterIdentitySchema = z.object({
  key: z.string().min(1),
  dimension: productCenterDimensionSchema,
  sku: z.string().nullable(),
  asin: z.string().nullable(),
  parentAsin: z.string().nullable(),
  title: z.string().nullable(),
  imageUrl: z.string().nullable(),
  brand: z.string().nullable(),
  category: z.string().nullable(),
  bestSellerRank: z.string().nullable(),
  availability: z.string().nullable(),
  listingStatus: z.array(z.string()),
  fulfillmentChannel: z.enum(["FBA", "FBM", "MIXED"]).nullable(),
  quantity: z.number().int().nonnegative().nullable(),
  priceAmount: z.number().nonnegative().nullable(),
  priceCurrency: z.string().nullable(),
  isActive: z.boolean(),
  productStatus: productStatusSchema,
});

export const productCenterProductRowSchema = z.object({
  shop: productCenterShopSchema.optional(),
  tacosEligible: z.boolean().optional(),
  identity: productCenterIdentitySchema,
  productLine: productLineSummarySchema.nullable(),
  ads: productCenterAdsMetricsSchema,
  seller: productCenterSellerMetricsSchema,
});
export type ProductCenterProductRow = z.infer<typeof productCenterProductRowSchema>;

export const productCenterProductsResponseSchema = z.object({
  success: z.literal(true),
  currency: z.string().regex(/^[A-Z]{3}$/),
  currencyApproximate: z.boolean().optional(),
  excludedShops: z.number().int().nonnegative().optional(),
  tacosEligibility: z.discriminatedUnion("status", [
    z.object({ status: z.literal("available") }),
    z.object({
      status: z.literal("unavailable"),
      reason: z.enum([
        "seller_not_connected",
        "seller_not_synced",
        "seller_partial",
        "ads_partial",
        "timezone_unverified",
        "currency_mismatch",
      ]),
    }),
  ]),
  data: z.array(productCenterProductRowSchema),
  summary: z.object({
    ads: productCenterAdsMetricsSchema,
    seller: productCenterSellerMetricsSchema,
  }),
  trend: z.array(
    z.object({
      date: isoDateSchema,
      ads: productCenterAdsMetricsSchema,
      seller: productCenterSellerMetricsSchema,
    }),
  ),
  coverage: z.object({
    ads: z.discriminatedUnion("status", [
      z.object({ status: z.literal("unavailable"), reason: z.literal("not_synced") }),
      z.object({
        status: z.enum(["available", "partial"]),
        startDate: isoDateSchema,
        endDate: isoDateSchema,
      }),
    ]),
    seller: z.discriminatedUnion("status", [
      z.object({
        status: z.literal("unavailable"),
        reason: z.enum(["not_connected", "not_synced", "currency_mismatch"]),
      }),
      z.object({
        status: z.enum(["available", "partial"]),
        startDate: isoDateSchema,
        endDate: isoDateSchema,
      }),
    ]),
  }),
  catalog: productCatalogStateSchema,
  pagination: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive().max(100),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
  }),
});
export type ProductCenterProductsResponse = z.infer<typeof productCenterProductsResponseSchema>;
